import { FormattedMessage, useIntl } from 'react-intl'
import { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import GoogleLongLogo from 'components/Common/TrustIndicators/GoogleLongLogo'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const SpreadTrustIndicators = styled(Group)`
  width: 100%;
  height: ${rem(40)};
  align-content: center;
`

const CompactTrustIndicators = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-wrap: wrap;
  column-gap: ${rem(10)};
  row-gap: ${rem(4)};
`

const SeparatorDot = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.regular};
  font-size: ${rem(14)};
  line-height: ${rem(20)};

  &::before {
    content: '\\00B7';
  }
`

interface Props {
  /**
   * Tablet and desktop flash offer sign-up row: 40px tall, clusters 16px apart.
   */
  spread?: boolean
  /**
   * Mobile flash offer sign-up review row: centred Google rating, dot, and member count.
   */
  compact?: boolean
}

function AccountAccessTrustIndicatorsMapper({ spread = false, compact = false }: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const atolProtected = currentRegionCode === 'GB' && <Group direction="horizontal" gap={4} verticalAlign="center">
    <img src={getSVGImageUrl('984lj4elpskyg828z90qb')} width={20} height={20} alt="" />
    <BodyTextBlock variant="medium" weight="bold" wrap="no-wrap">
      {intl.formatMessage({
        id: 'VxW64n9lfPWXpeyYxquy',
        defaultMessage: 'ATOL protected',
        description: 'ATOL protection label in UK trust indicator in the insurance flow',
      })}
    </BodyTextBlock>
  </Group>
  const googleRating = <Group direction="horizontal" gap={compact ? 4 : 8} verticalAlign="center">
    <GoogleLongLogo width="54" height="18" />
    <BodyTextBlock variant="medium" weight="bold" wrap="no-wrap">
      {intl.formatMessage({
        id: 'AFF9frfzVWiHNAba93vz',
        defaultMessage: '4.7/5',
        description: 'Google review rating score displayed on login modal',
      })}
    </BodyTextBlock>
    {!compact && <CSSBreakpoint min="desktop">
      <BodyText variant="small" colour="light-foreground-alt" wrap="no-wrap">
        {intl.formatMessage({
          id: 'f2iP-sfiwGgXwdk6j1qW',
          defaultMessage: '(4.5K reviews)',
          description: 'Google review count displayed on login modal in the reviews section',
        })}
      </BodyText>
    </CSSBreakpoint>}
  </Group>
  const memberCount = intl.formatMessage({
    id: 'dNtkovUEOoWPVfB1ibtU',
    defaultMessage: '15 million members',
    description: 'Member count displayed beneath membership benefits in login and offer sign-up panels',
  })

  if (compact) {
    return <CompactTrustIndicators>
      {atolProtected}
      {googleRating}
      <SeparatorDot aria-hidden="true" />
      <BodyText variant="medium" weight="normal" wrap="no-wrap">
        <FormattedMessage
          id="Jgtk3N2PEjYdeX4kfi06"
          defaultMessage="<bold>15M+</bold> members"
          description="Compact member count beside the Google rating in the flash hotel sign-up modal"
          values={{ bold: (chunks) => <b>{chunks}</b> }}
        />
      </BodyText>
    </CompactTrustIndicators>
  }

  const IndicatorRow = spread ? SpreadTrustIndicators : Group

  return (
    <IndicatorRow
      direction="horizontal"
      gap={spread ? 16 : 24}
      verticalAlign="center"
      horizontalAlign={spread ? 'start' : 'center'}
      wrap="wrap"
    >
      {atolProtected}
      {googleRating}
      <BodyTextBlock
        variant="medium"
        wrap="no-wrap"
        startIcon={<LineUsersAltIcon size="S" colour="primary" aria-hidden="true" />}
      >
        {memberCount}
      </BodyTextBlock>
    </IndicatorRow>
  )
}

export default AccountAccessTrustIndicatorsMapper
